// Admin subscription-link support tool. Mirrors
// API/api/src/subscriptions/admin-subscriptions.controller.ts and
// dto/admin-subscription.dto.ts — the server is authoritative for shapes.
// Dates travel as ISO strings over JSON (the server's Date fields serialize
// to strings), so every date field here is typed as `string`, not `Date`.

import { apiGet, apiPost, apiDelete } from './client';

export type SubscriptionStatus =
  | 'active'
  | 'expired'
  | 'in_trial'
  | 'grace_period'
  | 'revoked';

export interface PageMeta {
  page: number;
  limit: number;
  totalItems: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

export interface SubscriptionLinkRow {
  id: string;
  originalTransactionId: string;
  productId: string;
  userId: string;
  userEmail: string | null;
  userName: string | null;
  linkedAt: string;
  transferredFromUserId: string | null;
  transferredAt: string | null;
  createdAt: string;
  updatedAt: string;
  status: string | null;
  expiresAt: string | null;
  isInTrial: boolean | null;
  willAutoRenew: boolean | null;
  lastVerifiedAt: string | null;
}

export interface PaginatedSubscriptionLinks {
  data: SubscriptionLinkRow[];
  meta: PageMeta;
}

export interface SubscriptionLinksParams {
  page?: number;
  limit?: number;
  sortBy?: 'linkedAt' | 'createdAt' | 'updatedAt' | 'userEmail' | 'productId';
  sortDirection?: 'asc' | 'desc';
  productId?: string;
  status?: SubscriptionStatus;
  userId?: string;
  linkedAfter?: string;
  linkedBefore?: string;
  expiresAfter?: string;
  expiresBefore?: string;
  search?: string;
}

export interface TransferLogRow {
  id: string;
  originalTransactionId: string;
  fromUserId: string;
  fromUserEmail: string | null;
  toUserId: string;
  toUserEmail: string | null;
  transferredAt: string;
  ipAddress: string | null;
  userAgent: string | null;
}

export interface PaginatedTransferLog {
  data: TransferLogRow[];
  meta: PageMeta;
}

export interface TransferLogParams {
  page?: number;
  limit?: number;
  sortBy?: 'transferredAt' | 'fromUserEmail' | 'toUserEmail';
  sortDirection?: 'asc' | 'desc';
  originalTransactionId?: string;
  fromUserId?: string;
  toUserId?: string;
  transferredAfter?: string;
  transferredBefore?: string;
  search?: string;
}

export interface SubscriptionLinkDetail {
  id: string;
  originalTransactionId: string;
  productId: string;
  linkedAt: string;
  createdAt: string;
  updatedAt: string;
  user: {
    id: string;
    auth0Id: string;
    email: string | null;
    name: string | null;
    createdAt: string;
  };
  status: {
    status: string;
    purchaseDate: string | null;
    expiresAt: string | null;
    isInTrial: boolean;
    willAutoRenew: boolean;
    lastVerifiedAt: string;
  } | null;
  transferHistory: TransferLogRow[];
  transferredFromUserId: string | null;
  transferredAt: string | null;
}

export interface SubscriptionStats {
  totalLinks: number;
  totalStatuses: number;
  statusBreakdown: { status: string; count: number }[];
  productBreakdown: { productId: string; count: number; activeCount: number }[];
  totalTransfers: number;
  transfersLast30Days: number;
  linksLast7Days: number;
  linksLast30Days: number;
  expiringSoon: number;
  recentlyExpired: number;
  staleStatuses: number;
  activeTrials: number;
  averageDaysToLink: number | null;
}

export interface UnlinkResult {
  success: boolean;
  message: string;
}

export interface TransferResult {
  success: boolean;
  message: string;
  organizationId: string | null;
}

export interface VerifyResult {
  success: boolean;
  previousStatus: string | null;
  newStatus: string | null;
  message: string;
}

/** GET /admin/subscriptions/stats */
export const getSubscriptionStats = (
  params: { startDate?: string; endDate?: string; productId?: string } = {},
): Promise<SubscriptionStats> =>
  apiGet<SubscriptionStats>('/admin/subscriptions/stats', params);

/** GET /admin/subscriptions/links */
export const getSubscriptionLinks = (
  params: SubscriptionLinksParams = {},
): Promise<PaginatedSubscriptionLinks> =>
  apiGet<PaginatedSubscriptionLinks>('/admin/subscriptions/links', params);

/** GET /admin/subscriptions/links/:id */
export const getSubscriptionLinkById = (
  id: string,
): Promise<SubscriptionLinkDetail> =>
  apiGet<SubscriptionLinkDetail>(
    `/admin/subscriptions/links/${encodeURIComponent(id)}`,
  );

/** DELETE /admin/subscriptions/links/:id — admin unlink for support cases. */
export const unlinkSubscription = (id: string): Promise<UnlinkResult> =>
  apiDelete<UnlinkResult>(`/admin/subscriptions/links/${encodeURIComponent(id)}`);

/** POST /admin/subscriptions/links/:id/transfer */
export const transferSubscription = (
  id: string,
  toUserId: string,
  reason?: string,
): Promise<TransferResult> =>
  apiPost<TransferResult>(
    `/admin/subscriptions/links/${encodeURIComponent(id)}/transfer`,
    { toUserId, reason },
  );

/** POST /admin/subscriptions/transactions/:originalTransactionId/verify */
export const verifySubscription = (
  originalTransactionId: string,
): Promise<VerifyResult> =>
  apiPost<VerifyResult>(
    `/admin/subscriptions/transactions/${encodeURIComponent(originalTransactionId)}/verify`,
  );

/** GET /admin/subscriptions/transfers — audit log. */
export const getTransferLog = (
  params: TransferLogParams = {},
): Promise<PaginatedTransferLog> =>
  apiGet<PaginatedTransferLog>('/admin/subscriptions/transfers', params);

/** GET /admin/subscriptions/products — distinct product IDs, for filters. */
export const getSubscriptionProducts = (): Promise<string[]> =>
  apiGet<string[]>('/admin/subscriptions/products');

/** GET /admin/subscriptions/status-types — distinct statuses, for filters. */
export const getSubscriptionStatusTypes = (): Promise<string[]> =>
  apiGet<string[]>('/admin/subscriptions/status-types');
